// lib/operateur-constats-groupe.js — LE MÊME CONSTAT, DIT UNE FOIS.
//
// ⚠️ MIROIR OCTET-IDENTIQUE : src/lib/operateur-constats-groupe.js. Create
// React App refuse tout import hors de `src/` (ADR-021) ; un test compare les
// deux fichiers OCTET POUR OCTET. Si ce test casse, la réponse n'est pas de le
// relâcher : c'est de recopier le fichier.
//   cp lib/operateur-constats-groupe.js src/lib/operateur-constats-groupe.js
//
// ══ LA DEMANDE (Dimitri, 2026-09-16) ═══════════════════════════════════════
//
// « "Soleil du Sud — aucune réservation depuis 3 semaines" affiché deux fois,
// même heure, mot pour mot. Une phrase identique sur la même nature est le
// MÊME constat. On ne le recopie pas. »
//
// ══ POURQUOI LA CLÉ NE SUFFISAIT PAS ═══════════════════════════════════════
//
// La journée de l'écran (src/lib/evenements-jour.js) dédoublonne sur `cle`, et
// c'est tout ce qu'elle savait faire. Or une clé porte souvent un morceau que
// la PHRASE ne dit pas : le mois du jour (`prix_fige:<lid>:<prix>:2026-09`,
// `vitesse:<lid>:2026-09`), l'identifiant du dernier élément d'un lot et son
// compte (`prix_pousse:<id>:<n>`, `courriel_classe:<id>:<n>`), ou simplement
// l'identifiant d'un logement homonyme. Le fait ne bouge pas, la clé change —
// et deux lignes mot pour mot identiques s'installent côte à côte dans une
// journée qui, elle, s'accumule sur 48 h. Dédoublonner sur la clé ne pouvait
// pas les voir : c'est bien de DEUX clés qu'il s'agit.
//
// ══ LES DEUX NIVEAUX, ET CE QU'ILS N'ONT PAS LE DROIT DE FAIRE ═════════════
//
// 1 · NATURE + LES 120 PREMIERS CARACTÈRES DE LA PHRASE NUE. Deuxième
//     occurrence : on ne l'ajoute pas, on compte une seconde LECTURE sur la
//     première (`lectures`).
//
//     ⚠️ POURQUOI 120 ET PAS L'ÉGALITÉ STRICTE. Une même observation se
//     termine souvent par une queue qui bouge sans rien changer au fait (« —
//     c'est son plus long creux », « (sur ses séjours depuis le 4 janv.) »,
//     un arrondi de pourcentage). L'égalité stricte laisserait passer ces
//     jumelles ; une borne trop courte, à l'inverse, fondrait deux constats
//     qui commencent pareil et finissent ailleurs — les phrases de ce dépôt
//     commencent toutes par le nom du logement ou de la personne. 120
//     caractères, c'est au-delà de la tête commune et en deçà de la queue.
//
// 2 · ACTEUR + NATURE, ET SEULEMENT POUR TROIS NATURES (`message`,
//     `courriel`, `avis`) : quatre messages de la même personne sur le même
//     logement font une conversation, pas quatre faits. La ligne est alors
//     RÉÉCRITE (« 4 messages de Marie · Cocon Naturel »).
//
//     ⚠️ ET JAMAIS AU-DELÀ DE CES TROIS. Deux ménages sans agent, sur deux
//     logements, restent DEUX lignes : ce sont deux faits distincts, et les
//     fondre effacerait un ménage que personne ne ferait. Le groupement par
//     acteur ne s'applique qu'à ce qui est une conversation.
//
// ⚠️ CE QUE CETTE RÈGLE NE FAIT PAS. Elle ne fond pas deux faits différents
// qui se ressemblent : c'est le travail de `src/lib/operateur-a-traiter.js`,
// qui dédoublonne un ÉTAT et son ANNONCE sur l'identifiant du fait (« le même
// ménage vu de deux côtés »), jamais sur le texte. Les deux mécanismes se
// complètent et ne se remplacent pas.
//
// ⚠️ CE MODULE EST PUR. Pas d'horloge, pas de lecture, aucun effet : il prend
// une liste et en rend une autre. Il est appelé DEUX FOIS — à la source
// (lib/operateur-handlers.js, avant que la réponse ne parte) et à
// l'absorption (src/lib/evenements-jour.js, parce que la journée s'accumule
// sur 48 h et que deux lectures successives ne se rencontrent jamais côté
// serveur). Une seule règle, deux appels.

/** La borne de l'empreinte — voir le cartouche : ni l'égalité, ni la tête. */
const EMPREINTE_MAX = 120;

/** Les seules natures où deux faits du même acteur sont UNE conversation. */
const NATURES_GROUPABLES = new Set(["message", "courriel", "avis"]);

const MOT_PLURIEL = {
  message: "messages",
  courriel: "courriels",
  avis: "avis",
};

/** La phrase NUE : sans balise, sans espaces superflus. */
function _nu(phrase) {
  return String(phrase == null ? "" : phrase)
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * L'acteur nommé dans la phrase, ou null.
 *
 * ⚠️ LU SUR LES PHRASES RÉELLES, pas sur une fixture écrite pour passer. Les
 * phrases du serveur nomment la personne APRÈS « de » (« Message de Marie via
 * Airbnb — Cocon Naturel. », « Nouvel avis 5/5 de Marie — … ») ; un début de
 * phrase en majuscule, lui, n'est presque jamais un nom (« Message », « Un
 * e-mail », « Avis bas »). Sans nom, pas de groupement par acteur — la ligne
 * reste seule, ce qui est le repli qui ne perd rien.
 *
 * ⚠️ ON NE CHERCHE QUE DANS LA TÊTE, avant le premier « — » ou « : ». Deux
 * logements du parc s'appellent « Le Havre de Paix » et « L'Écrin de … » : lus
 * dans la queue de la phrase, ils fabriqueraient un acteur nommé « Paix », et
 * deux messages sans expéditeur se retrouveraient groupés sous un faux nom.
 */
function _qui(nu) {
  const m = nu
    .split(/ — | : /)[0]
    .match(
      /\bde\s+([A-ZÀ-ÖØ-Þ][\wÀ-ÖØ-öø-ÿ'’-]*(?:\s+[A-ZÀ-ÖØ-Þ][\wÀ-ÖØ-öø-ÿ'’-]*)?)/,
    );
  return m ? m[1] : null;
}

/**
 * Le lieu tel que la phrase le dit — pour la ligne réécrite uniquement.
 *
 * La CLÉ de groupe, elle, se fait sur `logement_id` quand il existe : un
 * identifiant ne se renomme pas. Ce qui suit ne sert qu'à écrire « · Cocon
 * Naturel » à la fin, et se tait dès qu'il n'est pas sûr de lui.
 */
function _lieu(nu) {
  const i = nu.lastIndexOf(" — ");
  if (i < 0) return null;
  let bout = nu.slice(i + 3);
  const j = bout.indexOf(" : ");
  if (j >= 0) bout = bout.slice(0, j);
  bout = bout
    .replace(/\s+(via|sur)\s+.*$/i, "")
    .replace(/[.,;]+$/, "")
    .trim();
  if (!bout || bout.length > 40) return null;
  return bout;
}

/** L'instant d'un fait, en millisecondes, ou null (jamais « maintenant »). */
function _instant(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string" || !v.trim()) return null;
  const ms = Date.parse(v.trim());
  return Number.isNaN(ms) ? null : ms;
}

/** « 14:20 » à Paris — le fuseau de l'app, jamais celui du serveur. */
function _heureParis(ms) {
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(ms));
}

/** Le nombre de lectures déjà portées par une ligne (1 par défaut). */
function _lectures(e) {
  const n = Number(e && e.lectures);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * grouperLesConstats — la liste, sans ses doublons de phrase.
 *
 * Le PREMIER de chaque groupe survit (il porte ses marques : `range`,
 * `traite`, `vu_le`) et hérite des signaux STRUCTURÉS des suivants — un
 * message urgent absorbé par un message calme ne doit pas faire redescendre
 * la ligne. `lectures` dit combien de fois le constat a été lu.
 *
 * @param {Array<object>} liste
 * @returns {Array<object>} une NOUVELLE liste, dans l'ordre d'entrée
 */
function grouperLesConstats(liste) {
  const groupes = new Map();
  const ordre = [];
  for (const brut of Array.isArray(liste) ? liste : []) {
    if (!brut || typeof brut !== "object") continue;
    // On repart TOUJOURS de la phrase d'origine : sans ça, une ligne déjà
    // réécrite (« 4 messages de Marie ») se regrouperait avec elle-même au
    // passage suivant, et le compte partirait en vrille.
    const phrase =
      typeof brut.phrase_origine === "string" && brut.phrase_origine
        ? brut.phrase_origine
        : brut.phrase;
    const nu = _nu(phrase);
    if (!nu) {
      // Rien à comparer : on la garde telle quelle plutôt que de la fondre
      // avec les autres muettes.
      ordre.push({ cles: [], membres: [{ ...brut, phrase, nu: "" }] });
      continue;
    }
    const nature = String(brut.nature || "");
    const exacte = `${nature}|${nu.slice(0, EMPREINTE_MAX)}`;
    const qui = _qui(nu);
    const parActeur =
      NATURES_GROUPABLES.has(nature) && qui
        ? `${nature}|${qui}|${brut.logement_id || _lieu(nu) || ""}`
        : null;
    const groupe =
      groupes.get(exacte) || (parActeur ? groupes.get(parActeur) : null);
    if (groupe) {
      groupe.membres.push({ ...brut, phrase, nu });
      // La clé exacte du nouveau venu rejoint le groupe : un troisième
      // message, identique au deuxième, doit le retrouver.
      if (!groupes.has(exacte)) {
        groupes.set(exacte, groupe);
        groupe.cles.push(exacte);
      }
      continue;
    }
    const neuf = { cles: [exacte], membres: [{ ...brut, phrase, nu }] };
    groupes.set(exacte, neuf);
    if (parActeur && !groupes.has(parActeur)) {
      groupes.set(parActeur, neuf);
      neuf.cles.push(parActeur);
    }
    ordre.push(neuf);
  }

  return ordre.map((groupe) => {
    const [premier] = groupe.membres;
    const { nu, ...garde } = premier; // eslint-disable-line no-unused-vars
    const lectures = groupe.membres.reduce((n, m) => n + _lectures(m), 0);
    // ⚠️ LE COMPTE FAIT FOI, PAS LE NOMBRE DE MEMBRES DU PASSAGE. Au passage
    // suivant, le survivant est SEUL (son doublon n'existe plus) et porte
    // `lectures: 2` : se fier à la taille du groupe rendrait la ligne à sa
    // phrase d'origine un rendu sur deux — un clignotement, pas une règle.
    if (lectures <= 1) return { ...garde, lectures };
    // ── CE QUI REMONTE DES ABSORBÉS ──────────────────────────────────────
    // Uniquement les signaux que la SOURCE pose (jamais lus dans le texte) :
    // les fondre au plus fort est la seule façon de ne pas perdre une urgence
    // en chemin.
    const montants = groupe.membres
      .map((m) => Number(m.montant))
      .filter((v) => Number.isFinite(v));
    const fondue = {
      ...garde,
      lectures,
      attend: groupe.membres.some((m) => m.attend === true),
      urgence: groupe.membres.some((m) => m.urgence === "haute")
        ? "haute"
        : garde.urgence || null,
      montant: montants.length ? Math.max(...montants) : null,
    };
    const nature = String(garde.nature || "");
    const qui = _qui(premier.nu);
    // ⚠️ UN CONSTAT RÉPÉTÉ RESTE UNE LIGNE, SANS COMPTE. Le répéter n'apprend
    // rien ; seule une conversation mérite « 4 messages de X ».
    if (!NATURES_GROUPABLES.has(nature) || !qui) return fondue;
    const lieu = _lieu(premier.nu);
    // L'heure du dernier du groupe se GARDE sur la ligne : au passage
    // suivant, les absorbés ne sont plus là pour la redire.
    const instants = groupe.membres
      .map((m) => [_instant(m.quand), _instant(m.dernier_quand)])
      .reduce((a, b) => a.concat(b), [])
      .filter((v) => v !== null);
    const dernier = instants.length ? Math.max(...instants) : null;
    return {
      ...fondue,
      phrase_origine: premier.phrase,
      dernier_quand: dernier === null ? null : new Date(dernier).toISOString(),
      phrase:
        `${lectures} ${MOT_PLURIEL[nature]} de ${qui}` +
        (lieu ? ` · ${lieu}` : "") +
        (dernier === null ? "." : ` — le dernier à ${_heureParis(dernier)}.`),
    };
  });
}

module.exports = {
  grouperLesConstats,
  EMPREINTE_MAX,
  NATURES_GROUPABLES,
  _internal: { _nu, _qui, _lieu, _lectures },
};
